import type { CourtDefinition } from '@/app/[locale]/admin/event-management/[eventId]/edit/scheduler/match-scheduler-types';
import type { EventVenueRow } from '@/models/eventVenueModel';
import type { EventHost, EventHostKind } from './eventHost';

/**
 * Event venues — the one place a court becomes a venue and a label.
 * `event.location_id` is the club the event is played at (the owner, who runs it, is `host`); the `event_venue` rows
 * (the Venues tab) are where it is played. Only an event with no rows falls
 * back to its own location as its one venue (`PRIMARY_VENUE_ID`, `isPrimary`,
 * never a DB row) — add a venue and the fallback is gone. A court with no
 * `venueId`, or a `venueId` that no longer exists, is at the first venue.
 * Label rule (spec §3): `Venue · Court` when the event has more than one venue,
 * the bare court name otherwise; unknown court ⇒ legacy `Court {id}` fallback.
 */
export const PRIMARY_VENUE_ID = 'primary';

export type VenueLocation = {
  id: string;
  name: string;
  address: string | null;
  logo: string | null;
  website: string | null;
  phone: string | null;
};
export type EventVenue = {
  id: string;
  name: string;
  address: string | null;
  location: {
    id: string;
    logo: string | null;
    website: string | null;
    phone: string | null;
  } | null;
  isPrimary: boolean;
  sortOrder: number;
  /**
   * Organiser-entered (Phase 4). OPTIONAL on purpose: many typed `EventVenue` test factories
   * across the repo predate these fields and must keep type-checking. `venueFromRow` always fills
   * them (null when unset); the event-location stand-in leaves them unset. Read with `?? null`.
   */
  photoUrl?: string | null;
  note?: string | null;
  /** Minutes to the next venue in the listed order; null/unset when not entered (and on the last venue). */
  travelMinToNext?: number | null;
};
export type EventVenueContext = {
  venues: EventVenue[];
  courts: CourtDefinition[];
  /** Who runs the event: its owner, a club or a federation (not its location). */
  host?: EventHost | null;
};

/** The one joiner between a venue name and a court name (U+00B7 with spaces): `Glass · Court 1`. */
export const VENUE_COURT_SEPARATOR = ' · ';
export const joinVenueCourt = (venue: string, court: string) =>
  `${venue}${VENUE_COURT_SEPARATOR}${court}`;

export function fallbackCourtName(courtId: string): string {
  return `Court ${courtId.replace('court-', '')}`;
}

function venueFromRow(row: EventVenueRow): EventVenue {
  const loc = row.location;
  return loc
    ? {
        id: row.id,
        name: loc.name,
        address: loc.address,
        location: { id: loc.id, logo: loc.logo, website: loc.website, phone: loc.phone },
        isPrimary: false,
        sortOrder: row.sort_order,
        photoUrl: row.photo_url ?? null,
        note: row.note ?? null,
        travelMinToNext: row.travel_min_to_next ?? null,
      }
    : {
        id: row.id,
        name: row.name ?? '',
        address: row.address,
        location: null,
        isPrimary: false,
        sortOrder: row.sort_order,
        photoUrl: row.photo_url ?? null,
        note: row.note ?? null,
        travelMinToNext: row.travel_min_to_next ?? null,
      };
}

export function buildVenueContext(input: {
  primary: VenueLocation | null;
  rows: EventVenueRow[];
  courts: CourtDefinition[];
  /** The event's owner. Absent: the location stands in as a club host (the pre-owner behaviour). */
  host?: EventHost | null;
}): EventVenueContext {
  const venues: EventVenue[] = [];
  // The event's own location stands in as the venue only while no venue is listed.
  if (input.primary && input.rows.length === 0) {
    const p = input.primary;
    venues.push({
      id: PRIMARY_VENUE_ID,
      name: p.name,
      address: p.address,
      location: { id: p.id, logo: p.logo, website: p.website, phone: p.phone },
      isPrimary: true,
      sortOrder: -1,
    });
  }
  // sort_order, created_at, id — the model's query order and the app RPC's (ties agree).
  const rows = [...input.rows].sort(
    (a, b) =>
      a.sort_order - b.sort_order ||
      a.created_at.localeCompare(b.created_at) ||
      (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
  for (const row of rows) venues.push(venueFromRow(row));
  const host: EventHost | null =
    input.host ??
    (input.primary ? { id: input.primary.id, name: input.primary.name, kind: 'club' } : null);
  return { venues, courts: input.courts, host };
}

export function emptyVenueContext(courts: CourtDefinition[]): EventVenueContext {
  return { venues: [], courts };
}

/**
 * What the public Venue card shows, kept apart so "who runs it" is never read as "where it is":
 * `hostName` / `hostKind` are the event's owner (a club or a federation); `venues` are only the
 * venues listed in the Venues tab. While none is listed, `hostPlace` carries the event location's
 * address and contacts (the place); once a venue is listed it is null. `hostIsPlace` is true when
 * the owner is a club, no venue is listed and that club is the event location: the card then shows
 * one "Hosted at" block instead of two.
 */
export function venueCardOf(ctx: EventVenueContext): {
  hostName: string | null;
  hostKind: EventHostKind | null;
  hostPlace: EventVenue | null;
  venues: EventVenue[];
  hostIsPlace: boolean;
} {
  const host = ctx.host ?? null;
  const hostPlace = ctx.venues.find((v) => v.isPrimary) ?? null;
  const venues = ctx.venues.filter((v) => !v.isPrimary);
  return {
    hostName: host?.name ?? null,
    hostKind: host?.kind ?? null,
    hostPlace,
    venues,
    hostIsPlace:
      host?.kind === 'club' &&
      venues.length === 0 &&
      hostPlace !== null &&
      hostPlace.location?.id === host.id,
  };
}

/** One-line "where" for cards and summaries: the first venue (`A +2` for more), else the name given (the place, not the owner). */
export function venueSummary(venueNames: string[], hostName: string | null): string | null {
  const names = venueNames.filter(Boolean);
  if (names.length === 0) return hostName || null;
  return names.length === 1 ? names[0]! : `${names[0]} +${names.length - 1}`;
}

export function venueSummaryOf(ctx: EventVenueContext): string | null {
  const card = venueCardOf(ctx);
  return venueSummary(
    card.venues.map((v) => v.name),
    card.hostPlace?.name ?? null,
  );
}

export function isMultiVenue(ctx: EventVenueContext): boolean {
  return ctx.venues.length > 1;
}

export function venueOfCourt(ctx: EventVenueContext, courtId: string | null): EventVenue | null {
  if (!courtId) return null;
  const court = ctx.courts.find((c) => c.id === courtId);
  if (!court) return null;
  return ctx.venues.find((v) => v.id === court.venueId) ?? ctx.venues[0] ?? null;
}

export function courtLabel(ctx: EventVenueContext, courtId: string | null): string {
  if (!courtId) return '';
  const court = ctx.courts.find((c) => c.id === courtId);
  if (!court) return fallbackCourtName(courtId);
  if (!isMultiVenue(ctx)) return court.name;
  const venue = venueOfCourt(ctx, courtId);
  return venue ? joinVenueCourt(venue.name, court.name) : court.name;
}

export function courtsAtVenue(ctx: EventVenueContext, venueId: string): CourtDefinition[] {
  return ctx.courts.filter((c) => venueOfCourt(ctx, c.id)?.id === venueId);
}

/** Courts with no venue set (or a removed one): they follow whichever venue is first. */
export function courtsFollowingFirstVenue(ctx: EventVenueContext): CourtDefinition[] {
  const ids = new Set(ctx.venues.map((v) => v.id));
  return ctx.courts.filter((c) => !c?.venueId || !ids.has(c.venueId));
}

const listedVenues = (ctx: EventVenueContext) => ctx.venues.filter((v) => !v.isPrimary);

/**
 * Names of the courts that removing `venueId` would silently hand to another listed venue
 * (courts with no venue set count toward the first venue). Empty when it is the only listed
 * venue: its courts go back to the event location, which is what removing it means.
 */
export function courtsBlockingVenueRemoval(ctx: EventVenueContext, venueId: string): string[] {
  if (listedVenues(ctx).length <= 1) return [];
  return courtsAtVenue(ctx, venueId).map((c) => c.name);
}

/** Names of the courts a new venue order (`ids`) would silently move: those following the first venue, when the first venue changes. */
export function courtsBlockingVenueReorder(ctx: EventVenueContext, ids: string[]): string[] {
  const listed = listedVenues(ctx);
  if (listed.length <= 1 || ids.length === 0 || ids[0] === listed[0]!.id) return [];
  return courtsFollowingFirstVenue(ctx).map((c) => c.name);
}

/**
 * Courts as a multi-venue event should store them: a court following the first venue is
 * pinned to it, so a later reorder or removal cannot move it. A one-venue event (or the
 * event-location stand-in) is left as it is — its courts have only one place to be.
 */
export function pinCourtsToVenues(ctx: EventVenueContext): CourtDefinition[] {
  const listed = listedVenues(ctx);
  if (listed.length <= 1) return ctx.courts;
  const following = new Set(courtsFollowingFirstVenue(ctx));
  return ctx.courts.map((c) => (following.has(c) ? { ...c, venueId: listed[0]!.id } : c));
}

/** Server-side write guard (spec §4.2): a venueId not in `knownIds` becomes null; returns the courts it touched. */
export function sanitiseCourtVenues(
  courts: CourtDefinition[],
  knownIds: Set<string>,
): { courts: CourtDefinition[]; dropped: string[] } {
  const dropped: string[] = [];
  const out = courts.map((c) => {
    if (!c?.venueId || knownIds.has(c.venueId)) return c;
    dropped.push(c.name);
    return { ...c, venueId: null };
  });
  return { courts: out, dropped };
}
